import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ScrollText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const TERMS_VERSION = '2026-03-01'; // Update this when terms change
const TERMS_UPDATE_DATE = new Date('2026-03-01T00:00:00Z'); // Date when T&Cs were updated

const TermsUpdateBanner = () => {
  const [show, setShow] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Only show for logged-in users who registered BEFORE the T&C update
    if (!user) {
      setShow(false);
      return;
    }

    // Check if user registered before the T&C update date
    const userCreatedAt = user.created_at ? new Date(user.created_at) : null;
    const isExistingUser = userCreatedAt && userCreatedAt < TERMS_UPDATE_DATE;

    if (!isExistingUser) {
      // New users already agreed during registration - don't show popup
      setShow(false);
      return;
    }

    // Check if user has already dismissed this version
    const dismissedVersion = localStorage.getItem('terms_dismissed_version');
    if (dismissedVersion !== TERMS_VERSION) {
      // Small delay before showing to not be intrusive on page load
      const timer = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleDismiss = () => {
    localStorage.setItem('terms_dismissed_version', TERMS_VERSION);
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-lg"
        >
          <div className="bg-white border-2 border-teal-500 rounded-lg shadow-xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-teal-100 rounded-full flex-shrink-0">
                <ScrollText className="h-5 w-5 text-teal-600" />
              </div>
              
              <div className="flex-1">
                <h3 className="font-semibold text-stone-900 mb-1">
                  Terms of Service Updated
                </h3>
                <p className="text-sm text-stone-600 mb-3">
                  We've updated our Terms of Service and Privacy Policy to improve clarity around Australian Consumer Law compliance, data protection, and seller requirements.
                </p>
                <div className="flex items-center gap-3">
                  <Link
                    to="/terms"
                    className="text-sm font-medium text-teal-600 hover:text-teal-700 underline"
                    onClick={handleDismiss}
                  >
                    Read Terms
                  </Link>
                  <Link
                    to="/privacy"
                    className="text-sm font-medium text-teal-600 hover:text-teal-700 underline"
                    onClick={handleDismiss}
                  >
                    Read Privacy Policy
                  </Link>
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  By continuing to use Crafters Market, you agree to the updated terms.
                </p>
              </div>
              
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-stone-100 rounded-full transition-colors flex-shrink-0"
                aria-label="Dismiss"
              >
                <X className="h-5 w-5 text-stone-400 hover:text-stone-600" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TermsUpdateBanner;
